import * as React from "react";
import * as globals from "../../../globals";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as modelsForm from "../../../models/form";
import * as utils from "../../../utils/utils";
import * as utilsViewMode from "../../../utils/viewmode";
import * as apiAccounts from "../../../api/accounts";
import * as apiSettings from "../../../api/settings";
import SettingsNav from "../nav";
import { AlertType, Alert } from "../../../components/alert";
import { CalendarViewMode } from "../../../enumerations";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { GroupBox } from "../../../components/groupBox";
import { FormGroup } from "../../../components/form";
import { TextBox } from "../../../components/textBox";
import { InputSize, InputSideButton } from "../../../components/input";
import { ComboBox, DROPDOWN_TEXT_ITEM_HEIGHT, ComboBoxType, DropDownTextItem } from "../../../components/comboBox";
import { CheckBox } from "../../../components/checkbox";
import { Button, ButtonType } from "../../../components/button";

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean;

    /**
     * Enthält den geöffneten Datensatz.
     */
    user?: modelsGet.IUser;

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        oldPassword?: string;
        newPassword?: string;
        newPasswordConfirmed?: string;
    };

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object;

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string;

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType;
}

export class UserProfileSecurity extends React.Component<{}, IState>
{
    constructor(props: any)
    {
        super(props);

        this.state = {
            isLoading: true,
            form: {},
            errors: {}
        };

        this.initAsync();
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync()
    {
        var user: modelsGet.IUser = await apiAccounts.getCurrentUser();

        this.setState((state: IState) => {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.user = user;

            return state;
        });
    }

    private onSaveClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { form } = this.state;
            var newData: modelsPost.IUserProfilePassword = {
                old_password: form.oldPassword,
                new_password: form.newPassword,
                new_password_confirmed: form.newPasswordConfirmed
            };

            await apiAccounts.changePassword(newData);

            this.setState({
                isLoading: false,
                errors: {},
                alertMessage: null
            });
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }
    
    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) => {
        this.setState((state: IState) => {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        var {
            isLoading,
            user,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1} />
            );
        }

        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="security" />
                    </div>
                    <div className="settings-content_alert">
                        <Alert
                            alertType={alertType}
                            isClosable={true}
                            isVisible={alertMessage != null}
                            onHide={() => this.setState({ alertMessage: null })}>
                            {alertMessage}
                        </Alert>
                    </div>
                    <div className="settings-others">
                        <div className="settings-others_content-wrapper">
                            <ScrollViewer
                                HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                                VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                                <div className="settings-others_content">
                                    <FormGroup>
                                        <TextBox
                                            label="Altes Passwort"
                                            value={form.oldPassword}
                                            size={InputSize.normal}
                                            autoFocus={true}
                                            isPassword={true}
                                            errorMessage={errors["OldPassword"]}
                                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "oldPassword")} />
                                        <TextBox
                                            label="Neues Passwort"
                                            value={form.newPassword}
                                            size={InputSize.normal}
                                            isPassword={true}
                                            errorMessage={errors["NewPassword"]}
                                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "newPassword")} />
                                        <TextBox
                                            label="Neues Passwort bestätigen"
                                            value={form.newPasswordConfirmed}
                                            size={InputSize.normal}
                                            isPassword={true}
                                            errorMessage={errors["NewPasswordConfirmed"]}
                                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "newPasswordConfirmed")} />
                                    </FormGroup>
                                </div>
                            </ScrollViewer>
                        </div>
                        <div className="settings-others_actions">
                            <Button
                                text="Speichern"
                                type={ButtonType.primary}
                                onClick={this.onSaveClick} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}